import { Badge } from '@staff-app/ui';
import { STRIPE_INVOICE_STATUS_TONE, type StripeInvoiceStatus } from '@staff-app/shared-types';

/**
 * STRIPE's invoice status, as a badge — matching the Stage badge beside it (requester,
 * 2026-09-10).
 *
 * ── IT WAS COLOURED TEXT, AND THAT WAS THE PROBLEM ─────────────────────────────────────────────
 *
 * Stage rendered as a badge and this rendered as a tinted word, so two columns describing the
 * same row looked like two different KINDS of thing. They are two axes of one truth — what we
 * have done, and what Stripe thinks of the money — and they should read as peers.
 *
 * ── THE TONES ──────────────────────────────────────────────────────────────────────────────────
 *
 *   paid          → the same filled treatment as `Order-created`, the other "this is finished"
 *                   state in the row. Asked for explicitly, and right: they are the two
 *                   outcomes somebody scanning the queue is happy to see.
 *   open          → ORANGE. Sent, awaiting payment: nothing is wrong, but somebody is waiting.
 *   uncollectible → RED. Written off. This is the "unpaid" the requester meant in the sense that
 *                   matters: money we are not going to get.
 *   draft         → GREY. Not yet the customer's problem.
 *   void          → GREY. Cancelled deliberately, so nothing is owed and nothing is wrong — red
 *                   here would raise an alarm about a decision somebody made on purpose.
 *
 * The grey comes from the `muted` tokens rather than `secondary`, which in this app is a pale
 * MINT — a "grey" badge built from it comes out green, which on an invoice reads as good news.
 *
 * ⚠️ THE WORD IS STRIPE'S AND IS NEVER RE-WORDED, only the colour is ours. `uncollectible` means
 * written off, not overdue, and an earlier version of the device-order code conflated those and
 * asserted something Stripe never said. Note also that Stripe has NO `unpaid` status: `open` IS
 * unpaid-and-awaiting-payment, and an invoice past its due date is still `open`. Showing
 * "overdue" would need the invoice's due date, which this feature does not yet capture.
 *
 * The tone map itself is `STRIPE_INVOICE_STATUS_TONE` in shared-types, so the filter, the table
 * and the panel cannot form three opinions about what `open` looks like.
 */
export function IntlInvoiceStatusBadge({
  status,
  /** Prefix the label, e.g. `Stripe: open` in the detail panel where context is thinner. */
  prefix,
}: {
  status?: string | null;
  prefix?: string;
}) {
  if (!status) {
    return (
      <span className="text-xs text-muted-foreground" data-testid="intl-invoice-status-none">
        {'—'}
      </span>
    );
  }

  const tone = STRIPE_INVOICE_STATUS_TONE[status as StripeInvoiceStatus] ?? 'neutral';
  // `default` for paid so it matches the Fulfilled stage badge exactly; a hand-rolled green
  // would be a second definition of "finished" that could drift from it.
  const variant = tone === 'good' ? 'default' : tone === 'bad' ? 'destructive' : 'outline';
  /**
   * The two tones the Badge component has no variant for.
   *
   * ORANGE for `open`. NOT `secondary`, which sounds like the neutral choice and is not: this
   * app's `--color-secondary` is a pale MINT (#e8f7f4 on #1ab394), so a "grey" badge built from
   * it comes out green — which on an invoice reads as good news.
   *
   * GREY for `draft` and `void` (requester, 2026-09-10), from the `muted` tokens, which are the
   * app's actual grey. Both mean "nothing is owed and nothing is wrong": one has not been sent,
   * the other was cancelled on purpose.
   */
  const warn = tone === 'warn' ? 'border-transparent bg-warning text-warning-foreground' : '';
  const grey = tone === 'neutral' ? 'border-transparent bg-muted text-muted-foreground' : '';

  return (
    <Badge
      variant={variant as never}
      className={`whitespace-nowrap ${warn} ${grey}`}
      data-testid={`intl-invoice-status-${status}`}
    >
      {prefix ? `${prefix} ${status}` : status}
    </Badge>
  );
}
